import React, { useRef, useEffect, useState } from 'react';
import { useSvgEditor } from './hook/useEditorSvg';
import { ISvgEditorExampleProps, INodeConfig } from './interface/svgEditor';
import SvgDisplay from './components/SvgDisplay';
import NodeEditor from './components/NodeEditor';
import WatermarkSetting from './components/WatermarkSetting';
import { Radio, Button, Modal } from '@umu/umu-design';
import '../../style/caet/editorSvg.scss';
import '../../style/caet/fonts/NotoSerif/notoSerifFonts.scss';
import dayjs from 'dayjs';

const { confirm } = Modal;
/*
<!-- 组织信息 -->
  <text x="100" y="22" text-anchor="middle" font-size="10" font-weight="bold" fill="#1976d2" style="display: none">${organization}</text>
  <!-- 用户名 -->
  <text x="100" y="48" text-anchor="middle" font-size="6" font-weight="bold" fill="#00407f" style="display: none">${userName}</text>
  <!-- 标题 -->
  <text x="100" y="68" text-anchor="middle" font-size="4.5" font-weight="bold" fill="white" style="display: none">${title}</text>
  <!-- 副标题 -->
  <text x="100" y="83" text-anchor="middle" font-size="6" fill="#00407f" style="display: none">${subTitle}</text>
  <!-- 证书信息 编号和日期 -->
  <text x="56" y="110" text-anchor="middle" font-weight="600" font-style="italic" font-size="3.5" fill="#00407f" style="display: none">${date}</text>
  <text x="22" y="126.5" text-anchor="left" font-size="2.5" fill="#00407f" style="display: none">${certificateId}</text>
  <!-- 二维码区域 -->
  <image x="173.55" y="115.55" width="14.2" height="14.2" href="${qr}" alt="qr" style="display: none"/>
  <!-- 验证链接区域 -->
  <text x="12" y="131" text-anchor="left" font-size="3" fill="#1e6ee6" style="cursor: pointer; text-decoration: underline;display: none" data-link="${link}" data-link-type="verification" >${link}</text>
  <!-- 隐藏验证信息 -->
  <!-- 防伪内容 - 四个角和中心位置，完全透明 -->
  <text x="200" y="200" text-anchor="left" font-size="12" fill="transparent" stroke="transparent" style="opacity: 0;" data-anti-counterfeiting="true">${antiCounterfeitingTop}</text>
  <text x="200" y="2330" text-anchor="left" font-size="12" fill="transparent" stroke="transparent" style="opacity: 0;" data-anti-counterfeiting="true">${antiCounterfeitingLeft}</text>
  <text x="3100" y="200" text-anchor="right" font-size="12" fill="transparent" stroke="transparent" style="opacity: 0;" data-anti-counterfeiting="true">${antiCounterfeitingRight}</text>
  <text x="3100" y="2330" text-anchor="right" font-size="12" fill="transparent" stroke="transparent" style="opacity: 0;" data-anti-counterfeiting="true">${antiCounterfeitingBottom}</text>
  <text x="1754" y="1260" text-anchor="middle" font-size="12" fill="transparent" stroke="transparent" style="opacity: 0;" data-anti-counterfeiting="true">${antiCounterfeitingCenter}</text>

*/
// 节点配置常量
export const NODE_CONFIGS: INodeConfig[] = [
    {
        key: 'title',
        label: '课程名称',
        placeholder: '请输入课程名称',
        defaultValue: '发展大模型时代的 AI 力',
        svgNodeConfig: {
            x: 100,
            y: 22,
        },
    },
    {
        key: 'subTitle',
        label: '认证名号',
        placeholder: '请输入认证名号',
        defaultValue: 'UMU Certified AI Empowered Talent',
        svgNodeConfig: {
            x: 100,
            y: 83,
        },
    },
    {
        key: 'userName',
        label: '用户姓名',
        placeholder: '请输入用户姓名',
        defaultValue: 'UMU 学员ワーク環繁體',
        svgNodeConfig: {
            x: 100,
            y: 48,
        },
    },
    {
        key: 'certificateId',
        label: '证书编号',
        placeholder: '请输入证书编号',
        defaultValue: '',
        svgNodeConfig: {
            x: 22,
            y: 126.5,
        },
    },
    {
        key: 'date',
        label: (
            <a href="https://tool.chinaz.com/tools/unixtime.aspx" target="_blank" rel="noreferrer">
                颁发日期[秒级时间戳]
            </a>
        ),
        placeholder: '请输入颁发日期',
        defaultValue: String(dayjs().unix()),
        svgNodeConfig: {
            x: 56,
            y: 110,
        },
    },
    {
        key: 'qr',
        label: '二维码链接',
        placeholder: '请输入网址链接（如：https://www.umu.cn/caet/10001）',
        defaultValue: 'https://www.umu.cn/caet/10001',
        svgNodeConfig: {
            x: 173.55,
            y: 115.55,
        },
    },
    {
        key: 'link',
        label: '链接地址',
        placeholder: '请输入链接地址（如：https://www.umu.cn/caet-verify/10001）',
        defaultValue: 'https://www.umu.cn/caet-verify/10001',
        svgNodeConfig: {
            x: 12,
            y: 131,
        },
    },
];
const SvgEditorExample: React.FC<ISvgEditorExampleProps> = ({ className = '' }) => {
    const svgContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // 系统选择状态：cn -> 中国站点, en -> 美国站点, jp -> 日本站点
    const [selectedSystem, setSelectedSystem] = useState<string>('cn');

    // 使用 SVG 编辑器 Hook
    const {
        nodeValuesRef,
        nodeList,
        initializeSvgEditor,
        updateNodeConfig,
        updateNodeList,
        handleFileUpload: handleFileUploadFromHook,
        handleInputChange,
        handleButtonClick,
        resetAllChanges,
        handleDownloadSvg,
        // 水印相关
        watermarkEnabled,
        watermarkConfigString,
        setWatermarkEnabled,
        setWatermarkConfigString,
        applyWatermark,
        updateNodeValues,
    } = useSvgEditor({ svgContainerRef, nodeConfigs: NODE_CONFIGS, currentSystem: selectedSystem });

    // 初始化
    useEffect(() => {
        initializeSvgEditor(selectedSystem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // 只在组件挂载时初始化

    // 处理系统切换
    const handleSystemChange = async (system: string) => {
        setSelectedSystem(system);
        // 重新加载模板并赋值
        await initializeSvgEditor(system);
        updateNodeValues();
    };

    // 处理文件上传
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;

        await handleFileUploadFromHook(
            file,
            // onError 回调
            (message: string) => {
                confirm({
                    title: '提示',
                    content: message,
                    okText: '确定',
                    centered: true,
                });
                // 清空文件输入
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            },
            // onSuccess 回调
            () => {
                // 清空文件输入
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            },
        );
    };

    // 触发文件选择
    const triggerFileUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div className={`svg-editor-container ${className}`}>
            <SvgDisplay svgContainerRef={svgContainerRef} />
            <h1 className="svg-editor-title">信息编辑</h1>
            {/* 系统选择 */}
            <div className="system-selector" style={{ marginBottom: '16px' }}>
                <span className="system-selector-label">选择站点：</span>
                <Radio
                    list={[
                        { label: '中国站点', key: 'cn' },
                        { label: '美国站点', key: 'en' },
                        { label: '日本站点', key: 'jp' },
                    ]}
                    active={selectedSystem}
                    onChange={handleSystemChange}
                    direction="horizontal"
                />
                <span className="system-selector-label" style={{ marginLeft: '24px' }}>
                    上传模版文件：
                </span>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".svg,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                />
                <Button type="default" onClick={triggerFileUpload}>
                    选择文件
                </Button>
            </div>
            {/* 水印设置 */}
            <WatermarkSetting
                watermarkEnabled={watermarkEnabled}
                watermarkConfigString={watermarkConfigString}
                setWatermarkEnabled={setWatermarkEnabled}
                setWatermarkConfigString={setWatermarkConfigString}
                applyWatermark={applyWatermark}
            />
            <NodeEditor
                nodeConfigs={nodeList}
                nodeValuesRef={nodeValuesRef}
                onInputChange={handleInputChange}
                onButtonClick={handleButtonClick}
                onReset={resetAllChanges}
                onDownload={handleDownloadSvg}
                updateNodeConfig={updateNodeConfig}
                updateNodeList={updateNodeList}
            />
        </div>
    );
};

export default SvgEditorExample;
